import { type ReactNode, createContext, useContext, useMemo } from 'react';

import type { PromptOption } from '@/react-query/videa-notes/usePrompts';

import type { VoiceNoteTemplateOption } from '../types';

/**
 * Recording context value interface.
 * Contains state and handlers for audio capture functionality.
 */
export interface VideaNotesRecordingContextValue {
  // Recorder state
  recorderStatus: string;
  mediaBlobUrl: string | undefined;

  // Prompt/Template selection
  promptOptions: PromptOption[];
  selectedPrompt: string | undefined;
  templateOptions: VoiceNoteTemplateOption[];
  selectedTemplate: string | undefined;
  onSelectedPrompt: (prompt: string | undefined) => void;
  onSelectedTemplate: (templateId: string | undefined) => void;

  // Recording feature flags
  isAudioUploadEnabled: boolean;
  showDevTools: boolean;

  // Recording handlers
  onMonitoringStreamChange: (stream: MediaStream | null) => void;
  onCancelRecording: () => void;
  onCancelRecordingConfirmation: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onStartRecording: () => void;
  onStartProviderOnlyRecording: () => void;
  onGenerateNote: (uploadedFile?: File, blobUrl?: string) => void;
  onPatientConsentDenied: () => void;
}

const VideaNotesRecordingContext =
  createContext<VideaNotesRecordingContextValue | null>(null);

interface VideaNotesRecordingProviderProps {
  value: VideaNotesRecordingContextValue;
  children: ReactNode;
}

/**
 * Provider component for VideaNotes recording context.
 * Contains state and handlers for audio capture functionality.
 */
export const VideaNotesRecordingProvider = ({
  value,
  children,
}: VideaNotesRecordingProviderProps) => {
  return (
    <VideaNotesRecordingContext.Provider value={value}>
      {children}
    </VideaNotesRecordingContext.Provider>
  );
};

/**
 * Hook to access VideaNotes recording context.
 * Must be used within a VideaNotesRecordingProvider.
 */
export const useVideaNotesRecordingContext =
  (): VideaNotesRecordingContextValue => {
    const context = useContext(VideaNotesRecordingContext);
    if (!context) {
      throw new Error(
        'useVideaNotesRecordingContext must be used within a VideaNotesRecordingProvider',
      );
    }
    return context;
  };

/**
 * Hook to access recorder state from recording context.
 * Memoized to prevent unnecessary re-renders.
 */
export const useRecorderState = () => {
  const { recorderStatus, mediaBlobUrl } = useVideaNotesRecordingContext();
  return useMemo(
    () => ({ recorderStatus, mediaBlobUrl }),
    [recorderStatus, mediaBlobUrl],
  );
};

/**
 * Hook to access prompt/template selection and feature flags from recording context.
 * Memoized to prevent unnecessary re-renders.
 */
export const useTemplateSelection = () => {
  const {
    promptOptions,
    selectedPrompt,
    templateOptions,
    selectedTemplate,
    onSelectedPrompt,
    onSelectedTemplate,
    isAudioUploadEnabled,
    showDevTools,
  } = useVideaNotesRecordingContext();
  return useMemo(
    () => ({
      promptOptions,
      selectedPrompt,
      templateOptions,
      selectedTemplate,
      onSelectedPrompt,
      onSelectedTemplate,
      isAudioUploadEnabled,
      showDevTools,
    }),
    [
      promptOptions,
      selectedPrompt,
      templateOptions,
      selectedTemplate,
      onSelectedPrompt,
      onSelectedTemplate,
      isAudioUploadEnabled,
      showDevTools,
    ],
  );
};

/**
 * Hook to access recording handlers from recording context.
 * Memoized to prevent unnecessary re-renders.
 */
export const useRecordingHandlers = () => {
  const {
    onMonitoringStreamChange,
    onCancelRecording,
    onCancelRecordingConfirmation,
    onPauseRecording,
    onResumeRecording,
    onStartRecording,
    onStartProviderOnlyRecording,
    onGenerateNote,
    onPatientConsentDenied,
  } = useVideaNotesRecordingContext();
  return useMemo(
    () => ({
      onMonitoringStreamChange,
      onCancelRecording,
      onCancelRecordingConfirmation,
      onPauseRecording,
      onResumeRecording,
      onStartRecording,
      onStartProviderOnlyRecording,
      onGenerateNote,
      onPatientConsentDenied,
    }),
    [
      onMonitoringStreamChange,
      onCancelRecording,
      onCancelRecordingConfirmation,
      onPauseRecording,
      onResumeRecording,
      onStartRecording,
      onStartProviderOnlyRecording,
      onGenerateNote,
      onPatientConsentDenied,
    ],
  );
};
